import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Clock, Flag, CreditCard, ChevronDown, Mail, Gift, Baby, Cake, Sparkles, User, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { BizouLogo, KissIcon } from "@/components/BizouLogo";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LoginDialog } from "@/components/LoginDialog";
import { useTranslation } from "@/i18n/LanguageContext";
import { API } from "@/App";

export default function LandingPage({ user, logout }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [bizouCount, setBizouCount] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [pendingReserve, setPendingReserve] = useState(false);
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [reserveDuration, setReserveDuration] = useState(10);

  const RESERVE_OPTIONS = [
    { years: 10, price: 29 },
    { years: 20, price: 49 },
    { years: 30, price: 79 },
  ];

  useEffect(() => {
    fetch(`${API}/stats/count`).then(r => r.json()).then(d => setBizouCount(d.count)).catch(() => {});
  }, []);

  const handleLogin = () => {
    setShowLogin(true);
  };

  const handleGetStarted = () => {
    if (window.gtag) window.gtag('event', 'create_message', { event_category: 'engagement', event_label: 'cta_click' });
    if (window.fbq) window.fbq('track', 'InitiateCheckout');
    if (user) navigate('/create');
    else handleLogin();
  };

  const doReserve = async () => {
    setReserveLoading(true);
    try {
      const response = await fetch(`${API}/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin, duration_years: reserveDuration }),
      });
      if (!response.ok) throw new Error("Failed to create reservation");
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
    } finally {
      setReserveLoading(false);
    }
  };

  const handleReserve = () => {
    if (!user) {
      setPendingReserve(true);
      handleLogin();
      return;
    }
    setShowReserveDialog(true);
  };

  const confirmReserve = () => {
    if (window.gtag) window.gtag('event', 'reserve_bizou', { event_category: 'engagement', event_label: 'reserve_confirm' });
    if (window.fbq) window.fbq('track', 'InitiateCheckout');
    setShowReserveDialog(false);
    doReserve();
  };

  // After login, if a reserve was pending, show the dialog
  useEffect(() => {
    if (user && pendingReserve) {
      setPendingReserve(false);
      setShowReserveDialog(true);
    }
  }, [user, pendingReserve]);

  const steps = [
    { icon: <Gift className="w-8 h-8 text-[#E8175D]" strokeWidth={1.5} />, title: t("landing.step1Title") },
    { icon: <Clock className="w-8 h-8 text-[#E8175D]" strokeWidth={1.5} />, title: t("landing.step2Title") },
    { icon: <KissIcon className="w-8 h-8 text-[#E8175D] kiss-pulse" />, title: t("landing.step3Title") },
  ];

  const trustItems = t("landing.trustItems");
  const trustIcons = [
    <Lock className="w-6 h-6 text-[#E8175D]" strokeWidth={1.5} />,
    <Clock className="w-6 h-6 text-[#E8175D]" strokeWidth={1.5} />,
    <Flag className="w-6 h-6 text-[#E8175D]" strokeWidth={1.5} />,
    <CreditCard className="w-6 h-6 text-[#E8175D]" strokeWidth={1.5} />,
  ];

  const emotionalTexts = t("landing.emotionalItems");
  const emotionalIcons = [
    <Baby className="w-5 h-5 text-[#E8175D]" strokeWidth={1.5} />,
    <KissIcon className="w-5 h-5 text-[#E8175D]" />,
    <Cake className="w-5 h-5 text-[#E8175D]" strokeWidth={1.5} />,
    <Sparkles className="w-5 h-5 text-[#E8175D]" strokeWidth={1.5} />,
  ];
  const emotionalItems = Array.isArray(emotionalTexts) ? emotionalTexts.map((text, i) => ({ icon: emotionalIcons[i], text })) : [];

  const faqItems = t("landing.faq");

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#F0E8EB]">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-14">
          <a href="/" className="flex items-center" data-testid="logo">
            <BizouLogo size="md" />
          </a>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            {user ? (
              <>
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-sm text-[#78716C] hover:text-[#1C1917] gap-2" data-testid="dashboard-btn">
                  <User className="w-4 h-4" />{t("common.mySpace")}
                </Button>
                <Button variant="ghost" onClick={logout} className="text-sm text-[#78716C] hover:text-[#1C1917]" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={handleLogin} className="text-sm text-[#78716C] hover:text-[#1C1917] gap-2" data-testid="login-btn">
                <User className="w-4 h-4" />{t("common.login")}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-4">
            <h1 className="inline-flex items-center justify-center" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}>
              <span className="text-6xl md:text-8xl text-[#1C1917]">BIZ</span>
              <img src="/kiss.png" alt="" className="w-auto h-12 md:h-16 kiss-pulse mx-[-2px]" />
              <span className="text-6xl md:text-8xl text-[#E8175D]">U</span>
              <span className="text-4xl md:text-6xl text-[#1C1917]">.me</span>
            </h1>
          </div>
          <p className="text-base md:text-lg text-[#78716C] mb-2">{t("landing.heroSubtitle")}</p>
          <p className="text-sm text-[#78716C]/60 mb-8">{t("landing.solutionBy")}</p>
          <Button onClick={handleGetStarted} className="h-14 px-10 rounded-full bg-[#E8175D] text-white text-lg font-medium hover:bg-[#C4134F] shadow-lg hover:shadow-xl active:scale-[0.98] group" data-testid="hero-cta-btn">
            <KissIcon className="w-5 h-5 mr-2 kiss-pulse" />
            {t("landing.ctaMain")}
          </Button>
          <div className="mt-10 relative cursor-pointer" onClick={handleGetStarted} data-testid="hero-envelope-cta">
            <div className="w-32 h-32 md:w-40 md:h-40 mx-auto rounded-full bg-[#E8175D]/5 flex items-center justify-center hover:bg-[#E8175D]/10 transition-colors">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#E8175D]/10 flex items-center justify-center relative hover:bg-[#E8175D]/15 transition-colors">
                <Mail className="w-10 h-10 md:w-12 md:h-12 text-[#E8175D]" strokeWidth={1.5} />
                <KissIcon className="w-5 h-5 md:w-6 md:h-6 text-[#E8175D] absolute -top-1 -right-1 kiss-pulse" />
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col items-center gap-2">
            <Button
              onClick={handleReserve}
              variant="outline"
              className="h-12 px-7 rounded-full border-2 border-[#E8175D] bg-white text-[#E8175D] text-base font-medium hover:bg-[#FDF0F4] hover:text-[#C4134F] hover:border-[#C4134F] shadow-sm hover:shadow-md active:scale-[0.98] transition-all"
              data-testid="reserve-cta-btn"
            >
              <Lock className="w-4 h-4 mr-2" strokeWidth={2} />
              {t("landing.ctaReserve")}
            </Button>
            <p className="text-xs text-[#78716C]/70 max-w-xs text-center">{t("landing.ctaReserveSubtitle")}</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-16 px-4 bg-[#FDF0F4]" data-testid="how-it-works">
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((s, i) => (
              <div key={i} className="text-center cursor-pointer group" onClick={handleGetStarted} data-testid={`step-${i + 1}`}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all">
                  {s.icon}
                </div>
                <p className="text-[#1C1917] font-medium text-lg group-hover:text-[#E8175D] transition-colors">{s.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMOTIONAL SECTION */}
      <section className="py-12 md:py-16 px-4" data-testid="emotional-section">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("landing.emotionalTitle").split("\n").map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}
          </h2>
          <div className="space-y-4 text-left max-w-sm mx-auto">
            {emotionalItems.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[#FDF0F4] cursor-pointer hover:bg-[#E8175D]/10 transition-colors" onClick={handleGetStarted}>
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">{item.icon}</div>
                <span className="text-[#1C1917]">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="py-10 px-4 bg-[#1C1917]" data-testid="trust-badges">
        <div className="max-w-3xl mx-auto">
          {bizouCount !== null && (
            <div className="text-center mb-8 pb-8 border-b border-white/10">
              <p className="text-4xl md:text-5xl font-medium text-[#E8175D]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="bizou-counter">
                {bizouCount.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
              </p>
              <p className="text-white/70 mt-2">{t("landing.bizousSent")}</p>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {Array.isArray(trustItems) && trustItems.map((text, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-3">{trustIcons[i]}</div>
                <span className="text-sm text-white/90 font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-12 md:py-16 px-4" id="pricing" data-testid="pricing-section">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-3xl border border-[#E7E5E4] shadow-lg p-8 text-center" data-testid="pricing-card">
            <div className="mb-6"><BizouLogo size="lg" className="justify-center" /></div>
            <ul className="text-left space-y-3 mb-8 text-[#1C1917]">
              {t("landing.pricingFeatures").map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <KissIcon className="w-4 h-4 text-[#E8175D]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mb-4">
              <span className="text-lg text-[#78716C]">{t("landing.pricingFrom")}</span><br />
              <span className="text-5xl font-medium text-[#E8175D]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="price">29 €</span>
            </div>
            <div className="flex gap-2 mb-6 text-xs" data-testid="landing-pricing-tiers">
              <div className="flex-1 p-2 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                <p className="font-bold">29€</p><p>10 {t("landing.tierUpTo")}</p>
              </div>
              <div className="flex-1 p-2 rounded-lg bg-violet-50 text-violet-700 border border-violet-200">
                <p className="font-bold">49€</p><p>20 {t("landing.tierUpTo")}</p>
              </div>
              <div className="flex-1 p-2 rounded-lg bg-[#FDF0F4] text-[#E8175D] border border-[#E8175D]/20">
                <p className="font-bold">79€</p><p>30 {t("landing.tierUpTo")}</p>
              </div>
            </div>
            <Button onClick={handleGetStarted} className="w-full h-14 rounded-full bg-[#E8175D] text-white text-lg font-medium hover:bg-[#C4134F] shadow-lg hover:shadow-xl" data-testid="pricing-cta-btn">
              <KissIcon className="w-5 h-5 mr-2 kiss-pulse" />{t("landing.ctaSend")}
            </Button>
          </div>
          {/* Reserve CTA — visible, just below the pricing card */}
          <div className="mt-6 flex flex-col items-center gap-2" data-testid="reserve-cta-pricing-wrap">
            <Button
              onClick={handleReserve}
              variant="outline"
              className="h-12 px-7 rounded-full border-2 border-[#E8175D] bg-white text-[#E8175D] text-base font-medium hover:bg-[#FDF0F4] hover:text-[#C4134F] hover:border-[#C4134F] shadow-sm hover:shadow-md active:scale-[0.98] transition-all"
              data-testid="reserve-cta-btn-pricing"
            >
              <Lock className="w-4 h-4 mr-2" strokeWidth={2} />
              {t("landing.ctaReserve")}
            </Button>
            <p className="text-xs text-[#78716C]/70 max-w-xs text-center">{t("landing.ctaReserveSubtitle")}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16 px-4 bg-[#FDF0F4]" data-testid="faq-section">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-center text-[#1C1917] mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("landing.faqTitle")}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {Array.isArray(faqItems) && faqItems.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl px-6 border-none">
                <AccordionTrigger className="text-left text-[#1C1917] font-medium hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-[#78716C] pb-5">
                  {item.a}
                  {item.link && item.link.url && (
                    <a
                      href={item.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-3 text-[#E8175D] hover:underline font-medium text-sm"
                      data-testid={`faq-link-${i}`}
                    >
                      {item.link.label || item.link.url} &rarr;
                    </a>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-20 px-4" data-testid="final-cta">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-xl md:text-2xl text-[#1C1917] mb-8 leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("landing.finalCta").split("\n").map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}
          </p>
          <Button onClick={handleGetStarted} className="h-14 px-10 rounded-full bg-[#E8175D] text-white text-lg font-medium hover:bg-[#C4134F] shadow-lg hover:shadow-xl active:scale-[0.98]" data-testid="final-cta-btn">
            <KissIcon className="w-5 h-5 mr-2 kiss-pulse" />{t("landing.ctaMain")}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#E7E5E4] bg-[#1C1917]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <BizouLogo size="sm" className="[&_span]:text-white [&_span:nth-child(3)]:text-[#E8175D]" />
          </div>
          <p className="text-sm text-white/50 mb-2">
            {t("landing.footerSolution")} <a href="http://www.my-memoire.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/70">My-Memoire.com</a>
          </p>
          <div className="flex items-center justify-center gap-4 mb-4">
            <a href="/privacy" className="text-sm text-white/40 hover:text-white/70 underline">{t("landing.footerPrivacy")}</a>
            <a href="/status" className="text-sm text-white/40 hover:text-white/70 underline" data-testid="footer-status-link">{t("landing.footerStatus")}</a>
            <button onClick={() => { import("@/components/CookieBanner").then(m => m.reopenCookieBanner()); }} className="text-sm text-white/40 hover:text-white/70 underline">{t("landing.footerCookies")}</button>
          </div>
          <div className="flex items-center justify-center gap-5 mb-4" data-testid="footer-social-links">
            <a
              href="https://www.instagram.com/bizou.me"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram-link"
              className="text-white/50 hover:text-[#E8175D] transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a
              href="https://x.com/BizouMeHQ"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
              data-testid="footer-x-link"
              className="text-white/50 hover:text-[#E8175D] transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@bizou.me"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              data-testid="footer-tiktok-link"
              className="text-white/50 hover:text-[#E8175D] transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7.04a4.85 4.85 0 0 1-1.84-.35z" />
              </svg>
            </a>
          </div>
          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} bizou.me
          </p>
        </div>
      </footer>
      {showLogin && <LoginDialog onClose={() => setShowLogin(false)} redirectPath={pendingReserve ? "/" : "/create"} />}

      <AlertDialog open={showReserveDialog} onOpenChange={setShowReserveDialog}>
        <AlertDialogContent className="max-w-md mx-4 rounded-2xl">
          <AlertDialogHeader className="text-left space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#FDF0F4] flex items-center justify-center">
              <Mail className="w-7 h-7 text-[#E8175D]" strokeWidth={1.5} />
            </div>
            <AlertDialogTitle className="text-center text-xl font-medium text-[#1C1917]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="reserve-dialog-title">
              {t("landing.reserveDialogTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-center text-[#57534e] leading-relaxed" data-testid="reserve-dialog-tagline">
                  {t("landing.reserveDialogTagline")}
                </p>
                <div className="bg-[#FDF0F4] rounded-xl p-4 space-y-2.5">
                  {(t("landing.reserveDialogBullets") || []).map((b, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="text-[#E8175D] mt-0.5 text-sm">&#10003;</span>
                      <span className="text-sm text-[#1C1917]">{b}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2" data-testid="reserve-duration-selector">
                  <p className="text-sm font-medium text-[#1C1917]">{t("landing.reserveDurationLabel")}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {RESERVE_OPTIONS.map((opt) => {
                      const active = reserveDuration === opt.years;
                      return (
                        <button
                          key={opt.years}
                          type="button"
                          onClick={() => setReserveDuration(opt.years)}
                          data-testid={`reserve-duration-${opt.years}`}
                          className={`rounded-xl px-3 py-3 text-center transition-all border-2 ${
                            active
                              ? "border-[#E8175D] bg-white shadow-[0_4px_14px_rgba(232,23,93,0.18)]"
                              : "border-[#F0E8EB] bg-white hover:border-[#E8175D]/40"
                          }`}
                        >
                          <div className={`text-xs uppercase tracking-wider mb-1 ${active ? "text-[#E8175D]" : "text-[#78716C]"}`}>
                            {opt.years} {lang === "fr" ? "ans" : "yrs"}
                          </div>
                          <div className="text-lg font-semibold text-[#1C1917]">
                            {opt.price} €
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <p className="text-center text-sm text-[#78716C]" data-testid="reserve-dialog-price">
                  {lang === "fr"
                    ? `Paiement unique de ${reserveDuration === 10 ? 29 : reserveDuration === 20 ? 49 : 79} € — conservation garantie ${reserveDuration} ans`
                    : `One-time payment of ${reserveDuration === 10 ? 29 : reserveDuration === 20 ? 49 : 79} € — guaranteed ${reserveDuration}-year storage`}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-3 sm:flex-col">
            <Button
              onClick={confirmReserve}
              disabled={reserveLoading}
              className="w-full bg-[#E8175D] text-white hover:bg-[#C4134F] rounded-full h-12 text-base font-medium"
              data-testid="reserve-confirm-btn"
            >
              {reserveLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              {t("landing.reserveDialogConfirm")}
            </Button>
            <AlertDialogCancel className="w-full rounded-full h-10 border-[#E8175D]/20 text-[#78716C]" data-testid="reserve-cancel-btn">
              {t("landing.reserveDialogLater")}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
